/**
 * BottomNav — the thumb-reachable bottom navigation (T12.1.5.1.1).
 *
 * Three slots, the same in both modes:
 *
 *   Planning:  List · Deals · Feedback
 *   Shopping:  List · Deals · Feedback
 *
 * List, Deals, and Feedback all exist in both modes, so no slot flips with the
 * mode and a mode switch can never strand the user on a route the new mode lacks
 * — there is no mode-exclusive nav route and no fallback redirect. Cards (the
 * loyalty wallet) and Trip are no longer nav tabs; their routes (`routes.cards`,
 * `routes.trip`) stay valid and are reached from affordances elsewhere.
 *
 * Fixed to the bottom, safe-area-aware (a native target is coming), ≥44px
 * targets. Part of the one responsive mobile-first shell — it renders at every
 * width. On wide screens it stays centered/aligned to the app column rather
 * than spanning the viewport (AppShell centers it via the `appshell-bottom-nav`
 * class; see AppShell § RESPONSIVE_CSS). Tokens-only (CP5).
 */

import { forwardRef, type CSSProperties } from 'react';
import { NavLink } from 'react-router-dom';
import { useAppMode } from '../stores/useAppMode';
import { routes } from '../routes/paths';
import { Icon, type IconName } from '../components';

const navStyle: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  // Chrome stays at page level (not card-elevated). In dark, cards lift a step
  // lighter than the page while the bottom nav reads as chrome at the page
  // surface (the proto relationship). In light, primary==raised==white → no-op.
  backgroundColor: 'var(--color-bg-primary)',
  borderTopWidth: '0.0625rem',
  borderTopStyle: 'solid',
  borderTopColor: 'var(--color-border-subtle)',
  // Honor the device safe-area inset (home indicator) on top of the base
  // height so the buttons never sit under the gesture bar on a phone.
  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  zIndex: 'var(--z-sticky)' as unknown as number,
};

const linkBase: CSSProperties = {
  flex: 1,
  minHeight: '3.5rem', // 56px row; the inner target clears 44px comfortably
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.125rem',
  textDecoration: 'none',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--color-text-muted)',
};

const linkActive: CSSProperties = {
  ...linkBase,
  color: 'var(--color-accent-primary)',
};

const iconStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  lineHeight: 1,
};

interface Slot {
  to: string;
  label: string;
  icon: IconName;
  testId: string;
}

const listSlot: Slot = {
  to: routes.home,
  label: 'List',
  icon: 'list',
  testId: 'bottom-nav-list',
};
const dealsSlot: Slot = {
  to: routes.deals,
  label: 'Deals',
  icon: 'tag',
  testId: 'bottom-nav-deals',
};
// T35.3 / F21 — the Feedback destination. Ambient: a nav slot the user chooses
// to visit; it never prompts or interrupts.
const feedbackSlot: Slot = {
  to: routes.feedback,
  label: 'Feedback',
  icon: 'chat',
  testId: 'bottom-nav-feedback',
};

// Forwards a ref to the root <nav> so AppShell can measure the nav's LIVE height
// (its base row + `env(safe-area-inset-bottom)` device inset) and publish it as
// `--app-bottomnav-height` — the offset a pinned element (DonationBanner) uses
// to clear the fixed nav on a device with a real safe-area inset.
export const BottomNav = forwardRef<HTMLElement>(function BottomNav(_props, ref): JSX.Element {
  const mode = useAppMode();

  const slots = [listSlot, dealsSlot, feedbackSlot];

  return (
    <nav
      ref={ref}
      style={navStyle}
      className="appshell-bottom-nav"
      data-testid="bottom-nav"
      aria-label="Primary"
      data-mode={mode}
    >
      {slots.map((slot) => (
        <NavLink
          key={slot.testId}
          to={slot.to}
          style={({ isActive }) => (isActive ? linkActive : linkBase)}
          data-testid={slot.testId}
          end={slot.to === routes.home}
        >
          <span style={iconStyle}>
            <Icon name={slot.icon} data-testid={`${slot.testId}-icon`} />
          </span>
          <span>{slot.label}</span>
        </NavLink>
      ))}
    </nav>
  );
});
